import { useViewport } from 'contexts/viewport';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Divider,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    RichTextArea,
    Select,
    Stack,
    styled,
    Text,
    TextArea,
    Tooltip,
} from 'shell/components';
import {
    CUSTOM_CONTENT_WORD_COUNT_LIMIT,
    CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT,
} from 'utils/constants';
import { getHTMLWordCount, getTooltipComponent, getWordCount, isFeedbackSettingsDefault } from 'utils/helper';
const getLanguageLevelText = (languageLevel, t) => {
    return {
        'middle-school': t(
            'feedback-settings.language-level.middle-school-description',
            'Middle School: Plain and straightforward feedback. Ideal for middle school students or those with basic knowledge.'
        ),
        'high-school': t(
            'feedback-settings.language-level.high-school-description',
            'High School: Moderately complex feedback. Suited for high school students or those with intermediate understanding.'
        ),
        undergraduate: t(
            'feedback-settings.language-level.undergraduate-description',
            'Undergraduate (Default): Complex feedback requiring higher-level understanding. Geared towards college-level learners or those with advanced knowledge.'
        ),
        graduate: t(
            'feedback-settings.language-level.graduate-description',
            'Graduate +: Very intricate feedback demanding advanced comprehension. Targeted at graduate students or individuals with expert-level understanding.'
        ),
    }[languageLevel];
};

const getToneText = (tone, t) => {
    return {
        'very-supportive': t(
            'feedback-settings.tone.very-supportive-description',
            'Very Supportive: Feedback is overwhelmingly positive and focuses primarily on strengths, offering minimal critique or suggestions for improvement.'
        ),
        supportive: t(
            'feedback-settings.tone.supportive-description',
            'Supportive: Feedback is generally positive, highlighting strengths while gently pointing out areas for improvement in a supportive manner.'
        ),
        balanced: t(
            'feedback-settings.tone.balanced-description',
            'Balanced (Default): Feedback strikes a balance between positive reinforcement and constructive criticism, providing both praise and suggestions for improvement in equal measure.'
        ),
        critical: t(
            'feedback-settings.tone.critical-description',
            'Critical:  Feedback is straightforward and analytical, pinpointing mistakes and areas needing improvement directly. It emphasizes the importance of addressing these areas to facilitate growth and development.'
        ),
    }[tone];
};

const FeedbackSettingsForm = ({ data, form, errors, handleChange }) => {
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const handleClickReset = () => {
        handleChange({
            numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
            suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
            languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
            tone: DEFAULT_FEEDBACK_TONE,
        });
    };

    if (form.structure === 'rubric') {
        return (
            <Stack sx={{ maxWidth: '800px' }} spacing={1}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'}>
                        {t(
                            'feedback-section.settings.rubric-based-feedback-settings',
                            'Rubric-Based Feedback Settings'
                        )}
                    </Text>
                    <Button
                        color={'neutral'}
                        disabled={isFeedbackSettingsDefault(form)}
                        variant={'borderless'}
                        onClick={handleClickReset}
                        sx={{ textAlign: 'right' }}
                    >
                        {t('reset-to-system-default', 'Reset to System Default')}
                    </Button>
                </Stack>
                <Paper variant={'outlined'}>
                    <Stack sx={device !== 'mobile' ? { position: 'relative' } : {}} spacing={7}>
                        <CustomSelect
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        {t('feedback-section.settings.suggestion-length', 'Length of Each Suggestion')}
                                    </Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t(
                                                'feedback-section.settings.suggestion-length',
                                                'Length of Each Suggestion'
                                            ),
                                            t(
                                                'feedback-section.settings.suggestion-length-tooltip',
                                                'Refers to the length of each unique suggestion provided to the student. For example, in cases of simple assignments where in-depth feedback is not crucial, choosing brief suggestions might help avoid overburdening the student.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'suggestion-length-select'}
                            value={form.suggestionLength}
                            onChange={(e) => handleChange({ suggestionLength: e.target.value })}
                        >
                            <MenuItem value={'<25'}>
                                {t('common:word-length.under-word-count', 'Under {{wordCount}} words', {
                                    wordCount: 25,
                                })}
                            </MenuItem>
                            <MenuItem value={'25-50'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '25-50',
                                })}
                            </MenuItem>
                            <MenuItem value={'50-150'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '50-150',
                                })}
                            </MenuItem>
                            <MenuItem value={'150-300'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '150-300',
                                })}
                            </MenuItem>
                            <MenuItem value={'300-450'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '300-450',
                                })}
                            </MenuItem>
                        </CustomSelect>
                        <Stack spacing={3}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {t('feedback-settings.language-level.title', 'Language Level')}
                                </Text>
                                <Tooltip
                                    content={getTooltipComponent(
                                        t('feedback-settings.language-level.title', 'Language Level'),
                                        t(
                                            'feedback-settings.language-level.feedback-tooltip',
                                            'Defines the language proficiency at which feedback is communicated. Each level is assessed based on the Flesch-Kincaid Grade Level Scale.'
                                        )
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            <CustomRadioGroup
                                aria-label={'language level'}
                                row={device !== 'mobile'}
                                value={form.languageLevel}
                                onChange={(e) => handleChange({ languageLevel: e.target.value })}
                            >
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t(
                                            'feedback-settings.language-level.middle-school-label',
                                            'Middle School'
                                        )}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'middle-school'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.language-level.high-school-label', 'High School')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'high-school'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t(
                                            'feedback-settings.language-level.undergraduate-label',
                                            'Undergraduate'
                                        )}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'undergraduate'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.language-level.graduate-label', 'Graduate +')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'graduate'}
                                    />
                                </CustomStack>
                            </CustomRadioGroup>
                            <Box
                                sx={{
                                    ...(device !== 'mobile' && {
                                        height: '32px',
                                    }),
                                }}
                            >
                                <Text>{getLanguageLevelText(form.languageLevel, t)}</Text>
                            </Box>
                        </Stack>
                        <Stack spacing={3}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {t('feedback-settings.tone.feedback-title', 'Feedback Tone')}
                                </Text>
                                <Tooltip
                                    content={getTooltipComponent(
                                        t('feedback-settings.tone.feedback-title', 'Feedback Tone'),
                                        t(
                                            'feedback-settings.tone.feedback-tooltip',
                                            'Describes how feedback is articulated, and whether it leans towards being supportive or critical, depending on the assignment’s demands and the student audience involved.'
                                        )
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            <CustomRadioGroup
                                aria-label={'feedback tone'}
                                row={device !== 'mobile'}
                                value={form.tone}
                                onChange={(e) => handleChange({ tone: e.target.value })}
                            >
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.very-supportive-label', 'Very Supportive')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'very-supportive'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.supportive-label', 'Supportive')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'supportive'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.balanced-label', 'Balanced')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'balanced'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.critical-label', 'Critical')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'critical'}
                                    />
                                </CustomStack>
                            </CustomRadioGroup>
                            <Text>{getToneText(form.tone, t)}</Text>
                        </Stack>
                        <RichTextArea
                            error={getHTMLWordCount(form.feedbackCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT}
                            helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                wordLimitStatus: `${getHTMLWordCount(
                                    form.feedbackCustomContext
                                )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                            })}
                            helperTextPosition={'end'}
                            label={t(
                                'feedback-settings.additional-instruction.label',
                                'Additional Instructions for the AI'
                            )}
                            placeholder={t(
                                'feedback-settings.additional-instruction.feedback-placeholder',
                                'Do you have any additional instructions about the content of feedback?'
                            )}
                            value={form.feedbackCustomContext}
                            onChange={(value) => handleChange({ feedbackCustomContext: value })}
                        />
                    </Stack>
                </Paper>
            </Stack>
        );
    }

    if (form.structure === 'system') {
        return (
            <Stack sx={{ maxWidth: '800px' }} spacing={1}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'}>
                        {t(
                            'feedback-section.settings.system-based-feedback-settings',
                            'General & Detailed Feedback Settings'
                        )}
                    </Text>
                    <Button
                        color={'neutral'}
                        disabled={isFeedbackSettingsDefault(form)}
                        variant={'borderless'}
                        onClick={handleClickReset}
                        sx={{ textAlign: 'right' }}
                    >
                        {t('reset-to-system-default', 'Reset to System Default')}
                    </Button>
                </Stack>
                <Paper variant={'outlined'}>
                    <Stack sx={device !== 'mobile' ? { position: 'relative' } : {}} spacing={7}>
                        <CustomSelect
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        {t('feedback-section.settings.number-of-suggestions', 'Number of Suggestions')}
                                    </Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t(
                                                'feedback-section.settings.number-of-suggestions',
                                                'Number of Suggestions'
                                            ),
                                            t(
                                                'feedback-section.settings.number-of-suggestions-tooltip',
                                                'Indicates the quantity of distinct recommendations in the feedback for guiding the student on how to enhance their work.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'num-suggestions-select'}
                            value={form.numSuggestions}
                            onChange={(e) => handleChange({ numSuggestions: e.target.value })}
                        >
                            <MenuItem value={'2'}>2</MenuItem>
                            <MenuItem value={'3'}>3 ({t('common:ui.default', 'Default')})</MenuItem>
                            <MenuItem value={'4'}>4</MenuItem>
                            <MenuItem value={'5'}>5 </MenuItem>
                        </CustomSelect>
                        <CustomSelect
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        {t('feedback-section.settings.suggestion-length', 'Length of Each Suggestion')}
                                    </Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t(
                                                'feedback-section.settings.suggestion-length',
                                                'Length of Each Suggestion'
                                            ),
                                            t(
                                                'feedback-section.settings.suggestion-length-tooltip',
                                                'Refers to the length of each unique suggestion provided to the student. For example, in cases of simple assignments where in-depth feedback is not crucial, choosing brief suggestions might help avoid overburdening the student.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'suggestion-length-select'}
                            value={form.suggestionLength}
                            onChange={(e) => handleChange({ suggestionLength: e.target.value })}
                        >
                            <MenuItem value={'<25'}>
                                {t('common:word-length.under-word-count', 'Under {{wordCount}} words', {
                                    wordCount: 25,
                                })}
                            </MenuItem>
                            <MenuItem value={'25-50'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '25-50',
                                })}
                            </MenuItem>
                            <MenuItem value={'50-150'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '50-150',
                                })}
                            </MenuItem>
                            <MenuItem value={'150-300'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '150-300',
                                })}
                            </MenuItem>
                            <MenuItem value={'300-450'}>
                                {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                    wordCountRange: '300-450',
                                })}
                            </MenuItem>
                        </CustomSelect>
                        <Stack spacing={3}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {t('feedback-settings.language-level.title', 'Language Level')}
                                </Text>
                                <Tooltip
                                    content={getTooltipComponent(
                                        t('feedback-settings.language-level.title', 'Language Level'),
                                        t(
                                            'feedback-settings.language-level.feedback-tooltip',
                                            'Defines the language proficiency at which feedback is communicated. Each level is assessed based on the Flesch-Kincaid Grade Level Scale.'
                                        )
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            <CustomRadioGroup
                                aria-label={'language level'}
                                row={device !== 'mobile'}
                                value={form.languageLevel}
                                onChange={(e) => handleChange({ languageLevel: e.target.value })}
                            >
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t(
                                            'feedback-settings.language-level.middle-school-label',
                                            'Middle School'
                                        )}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'middle-school'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.language-level.high-school-label', 'High School')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'high-school'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t(
                                            'feedback-settings.language-level.undergraduate-label',
                                            'Undergraduate'
                                        )}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'undergraduate'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.language-level.graduate-label', 'Graduate +')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'graduate'}
                                    />
                                </CustomStack>
                            </CustomRadioGroup>
                            <Box
                                sx={{
                                    ...(device !== 'mobile' && {
                                        height: '32px',
                                    }),
                                }}
                            >
                                <Text>{getLanguageLevelText(form.languageLevel, t)}</Text>
                            </Box>
                        </Stack>
                        <Stack spacing={3}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {t('feedback-settings.tone.feedback-title', 'Feedback Tone')}
                                </Text>
                                <Tooltip
                                    content={getTooltipComponent(
                                        t('feedback-settings.tone.feedback-title', 'Feedback Tone'),
                                        t(
                                            'feedback-settings.tone.feedback-tooltip',
                                            'Describes how feedback is articulated, and whether it leans towards being supportive or critical, depending on the assignment’s demands and the student audience involved.'
                                        )
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            <CustomRadioGroup
                                aria-label={'feedback tone'}
                                row={device !== 'mobile'}
                                value={form.tone}
                                onChange={(e) => handleChange({ tone: e.target.value })}
                            >
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.very-supportive-label', 'Very Supportive')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'very-supportive'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.supportive-label', 'Supportive')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'supportive'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.balanced-label', 'Balanced')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'balanced'}
                                    />
                                </CustomStack>
                                <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                    <Radio
                                        label={t('feedback-settings.tone.critical-label', 'Critical')}
                                        labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                        value={'critical'}
                                    />
                                </CustomStack>
                            </CustomRadioGroup>
                            <Text>{getToneText(form.tone, t)}</Text>
                        </Stack>
                        <RichTextArea
                            error={getHTMLWordCount(form.feedbackCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT}
                            helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                wordLimitStatus: `${getHTMLWordCount(
                                    form.feedbackCustomContext
                                )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                            })}
                            helperTextPosition={'end'}
                            label={t(
                                'feedback-settings.additional-instruction.label',
                                'Additional Instructions for the AI'
                            )}
                            placeholder={t(
                                'feedback-settings.additional-instruction.feedback-placeholder',
                                'Do you have any additional instructions about the content of feedback?'
                            )}
                            value={form.feedbackCustomContext}
                            onChange={(value) => handleChange({ feedbackCustomContext: value })}
                        />
                    </Stack>
                </Paper>
            </Stack>
        );
    }

    if (form.structure === 'templated') {
        const totalMaxPoints = data.rubric
            ? data.rubric.criteria
                  .map((criterion) => {
                      return Math.max(...criterion.options.map((option) => Number(option.rate)));
                  })
                  .reduce((acc, cur) => acc + cur, 0)
            : 0;

        return (
            <Stack spacing={1}>
                <Text variant={'p1'}>
                    {t('feedback-section.settings.templated-feedback-settings', 'Smart Feedback Templates Settings')}
                </Text>
                <Paper sx={{ position: 'relative', minHeight: '300px' }} variant={'outlined'}>
                    {!data.rubric ? (
                        <CenterText align={'center'} color={'light'}>
                            {t(
                                'feedback-section.settings.templated-feedback-missing-rubric',
                                'Please create a rubric in “General Assignment Details” to enable feedback templates.'
                            )}
                        </CenterText>
                    ) : (
                        <Stack spacing={5}>
                            <Text>
                                {t(
                                    'feedback-section.settings.templated-feedback-intro',
                                    'Smart Feedback Templates automatically apply feedback for each criterion based on the grade students receive. Please note that Instant Student Feedback and Feedback Preview are not available when this feature is enabled.'
                                )}
                            </Text>
                            <Stack spacing={2}>
                                <Text>{t('assignment-rubric-label', 'Assignment Rubric')}</Text>
                                <RootStack>
                                    <CustomBox>
                                        <Text variant={'p1'} weight={'semibold'}>
                                            {t('rubric.criteria', 'Criteria')}
                                        </Text>
                                    </CustomBox>
                                    <Stack>
                                        {data.rubric.criteria.map((criterion, criterionIndex) => {
                                            const criterionMaxPoints = Math.max(
                                                ...criterion.options.map((option) => Number(option.rate))
                                            );
                                            return (
                                                <Stack key={criterion.id}>
                                                    <CriterionHeaderStack spacing={2.5}>
                                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                                            <Text variant={'p2'}>{criterion.title}</Text>
                                                            <Text variant={'p2'}>
                                                                {t('rubric.criterion-points', 'Pts')}: -/
                                                                {criterionMaxPoints}
                                                            </Text>
                                                        </Stack>
                                                        <Text variant={'p3'}>{criterion.description}</Text>
                                                    </CriterionHeaderStack>
                                                    <OverflowStack
                                                        direction={device === 'mobile' ? 'column' : 'row'}
                                                        divider={
                                                            <Divider
                                                                flexItem
                                                                orientation={
                                                                    device === 'mobile' ? 'horizontal' : 'vertical'
                                                                }
                                                            />
                                                        }
                                                    >
                                                        {criterion.options.map((option, optionIndex) => (
                                                            <OptionStack
                                                                key={option.id}
                                                                spacing={3}
                                                                justifyContent={'space-between'}
                                                            >
                                                                <Stack spacing={3}>
                                                                    <Stack
                                                                        direction={'row'}
                                                                        justifyContent={'space-between'}
                                                                    >
                                                                        <Stack alignItems={'flex-start'} spacing={3}>
                                                                            <Text
                                                                                data-cy={`rubric-points-${option.rate}`}
                                                                                variant={'p2'}
                                                                            >
                                                                                {t(
                                                                                    'rubric.points-value',
                                                                                    '{{points}} pts',
                                                                                    {
                                                                                        points: option.rate,
                                                                                    }
                                                                                )}
                                                                            </Text>
                                                                            <Text variant={'p2'}>{option.title}</Text>
                                                                        </Stack>
                                                                    </Stack>
                                                                    <Text align={'left'} variant={'p3'}>
                                                                        {option.description}
                                                                    </Text>
                                                                </Stack>
                                                                <TextArea
                                                                    aria-label={`feedback for criterion ${criterionIndex + 1} option ${optionIndex + 1}`}
                                                                    error={
                                                                        errors.templated?.[criterion.id]?.[option.id]
                                                                    }
                                                                    helperText={t(
                                                                        'remaining-word-count',
                                                                        '{{wordLimitStatus}} words',
                                                                        {
                                                                            wordLimitStatus: `${getWordCount(
                                                                                form.templated?.[criterion.id]?.[
                                                                                    option.id
                                                                                ]
                                                                            )}/${RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT}`,
                                                                        }
                                                                    )}
                                                                    label={t(
                                                                        'feedback-section.settings.templated-feedback-additional-label',
                                                                        'Feedback for this rating'
                                                                    )}
                                                                    placeholder={t(
                                                                        'feedback-section.settings.templated-feedback-additional-placeholder',
                                                                        'Enter feedback'
                                                                    )}
                                                                    required
                                                                    rows={6}
                                                                    value={form.templated?.[criterion.id]?.[option.id]}
                                                                    onChange={(e) => {
                                                                        handleChange((current) => ({
                                                                            ...current,
                                                                            templated: {
                                                                                ...(current.templated || {}),
                                                                                [criterion.id]: {
                                                                                    ...(current.templated?.[
                                                                                        criterion.id
                                                                                    ] || {}),
                                                                                    [option.id]: e.target.value,
                                                                                },
                                                                            },
                                                                        }));
                                                                    }}
                                                                />
                                                            </OptionStack>
                                                        ))}
                                                    </OverflowStack>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                    <FooterBox>
                                        <Stack
                                            direction={'row'}
                                            divider={<Divider flexItem orientation={'vertical'} />}
                                        >
                                            <CustomText sx={{ flex: 1 }} align={'right'} variant={'p2'}>
                                                {t('rubric.total-points-out-of', 'Total Points Out of {{points}}', {
                                                    points: totalMaxPoints,
                                                })}
                                            </CustomText>
                                            <CustomText>-</CustomText>
                                        </Stack>
                                    </FooterBox>
                                </RootStack>
                            </Stack>
                        </Stack>
                    )}
                </Paper>
            </Stack>
        );
    }

    if (form.structure === 'custom') {
        return (
            <Stack sx={{ maxWidth: '800px' }} spacing={1}>
                <Text variant={'p1'}>
                    {t('feedback-section.settings.custom-feedback-settings', 'Custom Feedback Settings')}
                </Text>
                <Paper variant={'outlined'}>
                    <Stack spacing={5}>
                        <Text>
                            {t(
                                'feedback-section.settings.custom-feedback-intro',
                                "Please note that custom feedback instructions supersede our system defaults, so we cannot assure the quality or format of the output. It's your responsibility to outline the structure, tone, and content of the feedback. Make sure to be as specific as possible about your requirements."
                            )}
                        </Text>
                        <CustomRichTextArea
                            error={
                                errors.customInstructions ||
                                Boolean(
                                    getHTMLWordCount(form.customInstructions) > CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT
                                )
                            }
                            helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                wordLimitStatus: `${getHTMLWordCount(
                                    form.customInstructions
                                )}/${CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT}`,
                            })}
                            helperTextPosition={'end'}
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text>
                                        {t('feedback-section.settings.instruction-label', 'Instructions for the AI')}
                                    </Text>
                                    <CustomTooltip
                                        content={t(
                                            'feedback-section.settings.instruction-tooltip',
                                            'Specify the structure, tone, and content of the feedback here. Make sure to be as specific as possible about your requirements.'
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            placeholder={t(
                                'feedback-section.settings.instruction-placeholder',
                                'Provide instructions about the structure, tone and content of feedback here.'
                            )}
                            value={form.customInstructions}
                            onChange={(html) => handleChange({ customInstructions: html })}
                        />
                    </Stack>
                </Paper>
            </Stack>
        );
    }

    return null;
};

const CustomSelect = styled(Select)(() => {
    return {
        '& > .MuiInputBase-root': {
            width: '150px',
        },
    };
});

const CustomRadioGroup = styled(RadioGroup)(() => {
    return {
        justifyContent: 'space-between',
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        width: '150px',
        '& > .MuiFormControlLabel-root': {
            width: 'fit-content',
        },
    };
});

const CenterText = styled(Text)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '100%',
        transform: 'translate(-50%, -50%)',
    };
});

const RootStack = styled(Stack)(({ theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '12px',
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.primary[50],
    };
});

const CriterionHeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        padding: theme.spacing(2.5),
        background: theme.palette.ensightful.neutral[100],
    };
});

const OverflowStack = styled(Stack)(({ theme }) => {
    return {
        overflowX: 'auto',
        '&:focus-visible': {
            zIndex: 1,
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const OptionStack = styled(Stack)(({ theme }) => {
    return {
        flex: 1,
        minWidth: '240px',
        minHeight: '100%',
        padding: theme.spacing(3),
    };
});

const FooterBox = styled(Box)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '0 0 12px 12px',
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        '& > .MuiRichTextArea-root': {
            minHeight: '10rem',
        },
    };
});

const CustomTooltip = styled(Tooltip)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

export default FeedbackSettingsForm;
